import { cn } from '@/lib/utils';
import { PathEnum } from '@/types/enums';
import { useTranslations } from 'next-intl';
import Image from 'next/image';
import { JSXElementConstructor, ReactElement, ReactNodeArray } from 'react';
import Button from '../ui/Button';
import H3 from '../ui/H3';
import Link from '../ui/Link';
import P from '../ui/P';
import OurGuarantee from './OurGuarantee';
import Q1CertificatePanel from './Q1CertificatePanel';

export default function AboutSectionTabContent({
    imageSrc,
    title,
    certificates,
    description,
    services,
    imageLink,
    certificatePdf,
    downloadCertificateLabel,
}: {
    imageSrc: string;
    title: string | ReactElement<any, string | JSXElementConstructor<any>> | ReactNodeArray;
    certificates?: { title: string; icon: ReactElement; href: string }[];
    description: string;
    services: { title: string; description: string; imageSrc: string }[];
    imageLink?: string;
    certificatePdf?: string;
    downloadCertificateLabel?: string;
}) {
    const t = useTranslations();

    return (
        <div className="flex flex-col">
            <div className="flex max-lg:flex-col justify-between gap-7 lg:gap-[118px] lg:py-[80px] pt-20 pb-[60px] border-b border-light-grey">
                <div className="lg:w-1/2 w-full">
                    <h2 className="text-h2 max-lg:text-h4 leading-tight hidden max-lg:block">
                        {title}
                    </h2>
                    <div
                        className={cn(
                            'relative w-full max-lg:mt-7',
                            !(certificatePdf && downloadCertificateLabel) &&
                                'h-[206px] lg:h-[430px]',
                        )}
                    >
                        {certificatePdf && downloadCertificateLabel ? (
                            <Q1CertificatePanel
                                pdf={certificatePdf}
                                downloadLabel={downloadCertificateLabel}
                            />
                        ) : imageLink ? (
                            <a href={imageLink} target="_blank">
                                <Image
                                    className="object-contain object-left drop-shadow-sm"
                                    src={imageSrc}
                                    alt="about"
                                    fill
                                />
                            </a>
                        ) : (
                            <Image
                                className="object-contain object-left drop-shadow-sm"
                                src={imageSrc}
                                alt="about"
                                fill
                            />
                        )}
                    </div>
                </div>
                <div className="flex flex-col lg:w-1/2 w-full">
                    <h2 className="text-h2 max-lg:text-h4 leading-tight max-lg:hidden">{title}</h2>
                    {certificates &&
                        certificates.map(({ title, icon, href }, index) => (
                            <a
                                className="inline-block max-xs:w-full w-fit"
                                href={href}
                                target="_blank"
                                key={index}
                            >
                                <Button
                                    className="mt-6 w-full"
                                    textClasses="truncate"
                                    variant="upload"
                                    title={title}
                                    inverted
                                >
                                    {icon}
                                </Button>
                            </a>
                        ))}
                    <p className={cn('lg:text-h5 text-sm mt-7', !certificates && 'lg:mt-[120px]')}>
                        {description}
                    </p>
                    <Link className="get-offer-trigger w-fit mt-7 lg:mt-10" href={PathEnum.Contact}>
                        <Button title={t('Buttons.getOffer')} withArrow variant="secondary" />
                    </Link>
                </div>
            </div>
            <div className="flex max-xl:flex-col lg:pt-20 pt-[60px] justify-between lg:gap-[84px] gap-[60px]">
                {services.map(({ imageSrc, title, description }, index) => (
                    <div className="w-full xl:w-1/3 flex lg:gap-10 gap-4" key={index}>
                        <div>
                            <Image
                                className="max-lg:hidden object-contain min-w-[150px] h-[160px]"
                                src={imageSrc}
                                alt="Service"
                                width={150}
                                height={160}
                            />
                            <Image
                                className="lg:hidden object-contain min-w-[110px] h-[120px]"
                                src={imageSrc}
                                alt="Service"
                                width={110}
                                height={120}
                            />
                        </div>
                        <div className="flex flex-col gap-2 w-fit leading-tight">
                            <H3>{title}</H3>
                            <P>{description}</P>
                        </div>
                    </div>
                ))}
                <div className="w-full lg:w-max [&>div]:mt-4 [&>div]:gap-2">
                    <OurGuarantee
                        titleClassName="lg:text-h5 lg:mt-0 mt-0"
                        isAboutSection
                    />
                </div>
            </div>
        </div>
    );
}
